import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { User } from 'src/typings';
import { MoneyDayService } from '../money-flow/money-day.service';
import { UserService } from '../services/user.service';
import { createRequestId } from '../shared/utils/request-id.util';
import {
  CustomerAccountCommand,
  CustomerAccountInvoice,
  CustomerAccountKind,
  CustomerAccountOperation,
  CustomerAccountPreview,
  CustomerAccountService,
  CustomerAccountSummary,
  CustomerAccountChannel
} from './customer-account.service';
import {
  accountDate,
  accountError,
  accountKindLabel,
  accountMoney
} from './customer-account.utils';

export interface CustomerAccountOperationData {
  clientId: number;
  clientName?: string;
  orgId: number;
  orgName?: string;
  kind: CustomerAccountKind;
  summary?: CustomerAccountSummary;
  sourceOperation?: CustomerAccountOperation;
  orderId?: number;
  invoiceId?: number;
}

@Component({
  selector: 'app-customer-account-operation',
  templateUrl: './customer-account-operation.component.html',
  styleUrls: ['./customer-account.scss']
})
export class CustomerAccountOperationComponent implements OnInit, OnDestroy {
  form = this.fb.group({
    amount: [null, [Validators.required, Validators.min(0.01)]],
    channel: ['CASH'],
    cashbox_id: [null],
    bank_id: [null],
    org_bank_account_id: [null],
    bank_reference: [''],
    bank_date: [new Date()],
    reason: ['']
  });
  summary?: CustomerAccountSummary;
  preview?: CustomerAccountPreview;
  invoices: Array<CustomerAccountInvoice & { selectedAmount: number }> = [];
  channels: CustomerAccountChannel[] = [];
  get cashChannels() {
    return this.channels.filter((channel) => channel.channel === 'CASH');
  }
  get bankChannels() {
    return this.channels.filter((channel) => channel.channel === 'BANK');
  }
  get bankChannelKey() {
    return `${this.form.value.bank_id || ''}:${this.form.value.org_bank_account_id || ''}`;
  }
  user?: User;
  loading = true;
  busy = false;
  uncertain = false;
  error = '';
  contextError = '';
  contextLabel = '';
  private subscription = new Subscription();
  private request?: { request_id: string; token: string; version: number };
  private destroyed = false;
  readonly money = accountMoney;
  get title() {
    return accountKindLabel(this.data.kind);
  }
  get confirmLabel() {
    if (this.uncertain) return 'Проверить операцию';
    const amount = Number(this.preview?.plan?.amount || this.total || 0).toFixed(2);
    if (this.data.kind === 'TOPUP')
      return this.isCash ? `Принять ${amount} грн` : `Подготовить пополнение ${amount} грн`;
    if (this.data.kind === 'PAYOUT')
      return this.isCash ? `Выдать ${amount} грн` : `Подготовить выплату ${amount} грн`;
    if (this.isAllocation) return `Зачесть ${amount} грн`;
    if (this.isConfirmation) return `Подтвердить исполнение ${amount} грн`;
    if (this.isCancellation) return 'Отменить заявку';
    return `Освободить ${amount} грн`;
  }
  get isAllocation() {
    return this.data.kind === 'ALLOCATE';
  }
  get isConfirmation() {
    return this.data.kind === 'CONFIRM_BANK';
  }
  get isCancellation() {
    return this.data.kind === 'CANCEL_PENDING';
  }
  get isReversal() {
    return this.data.kind === 'REVERSE';
  }
  get requiresAmount() {
    return !this.isConfirmation && !this.isCancellation && !this.isReversal && !this.isAllocation;
  }
  get isMoneyMovement() {
    return this.data.kind === 'TOPUP' || this.data.kind === 'PAYOUT';
  }
  get isCash() {
    return this.form.value.channel === 'CASH';
  }
  get total() {
    return this.isAllocation
      ? accountMoney(
          this.invoices.reduce((sum, invoice) => sum + Number(invoice.selectedAmount || 0), 0)
        )
      : accountMoney(this.form.value.amount);
  }
  get canPreview() {
    return (
      !this.loading &&
      !this.busy &&
      !this.uncertain &&
      (!this.requiresAmount || this.form.controls.amount.valid) &&
      (!this.isAllocation || this.total > 0) &&
      (!this.isMoneyMovement || !this.contextError)
    );
  }
  get canExecute() {
    return !this.busy && Boolean(this.preview?.allowed) && !this.expired;
  }
  get expired() {
    return Boolean(
      this.preview?.expires_at && new Date(this.preview.expires_at).getTime() <= Date.now()
    );
  }

  constructor(
    private fb: UntypedFormBuilder,
    private accounts: CustomerAccountService,
    private moneyDay: MoneyDayService,
    private users: UserService,
    public ref: MatDialogRef<CustomerAccountOperationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CustomerAccountOperationData
  ) {
    this.summary = data.summary;
  }

  async ngOnInit() {
    this.subscription.add(this.form.valueChanges.subscribe(() => this.invalidate()));
    this.subscription.add(this.users.user.subscribe((user) => (this.user = user)));
    this.subscription.add(
      this.form.controls.channel.valueChanges.subscribe(() => this.updateContext())
    );
    if (this.data.sourceOperation)
      this.form.patchValue({
        amount: this.data.sourceOperation.amount,
        channel: this.data.sourceOperation.channel || 'BANK',
        bank_id: this.data.sourceOperation.bank_id,
        org_bank_account_id: this.data.sourceOperation.org_bank_account_id
      });
    try {
      this.summary = await firstValueFrom(
        this.accounts.summary(this.data.clientId, this.data.orgId)
      );
      if (this.isAllocation) {
        const response = await firstValueFrom(
          this.accounts.invoices(this.data.clientId, this.data.orgId)
        );
        this.invoices = (response.data || []).map((invoice) => ({ ...invoice, selectedAmount: 0 }));
        if (this.data.orderId || this.data.invoiceId)
          this.invoices = this.invoices.filter((invoice) =>
            this.data.invoiceId
              ? invoice.invoice_id === this.data.invoiceId
              : invoice.order_id === this.data.orderId
          );
      }
      if (this.isMoneyMovement || this.isConfirmation) {
        this.channels = (await firstValueFrom(this.accounts.channels(this.data.orgId))).data || [];
        await this.updateContext();
      }
    } catch (error) {
      this.error = accountError(error, 'Не удалось получить актуальный расчёт');
    } finally {
      if (!this.destroyed) this.loading = false;
    }
  }
  ngOnDestroy() {
    this.destroyed = true;
    this.subscription.unsubscribe();
  }
  invalidate() {
    if (!this.uncertain) {
      this.preview = undefined;
      this.request = undefined;
      this.error = '';
    }
  }
  setAllocation(invoice: CustomerAccountInvoice & { selectedAmount: number }, value: string) {
    invoice.selectedAmount = Math.max(accountMoney(value), 0);
    this.invalidate();
  }
  fillAllocation(invoice: CustomerAccountInvoice & { selectedAmount: number }) {
    const other = this.total - Number(invoice.selectedAmount || 0);
    invoice.selectedAmount = accountMoney(
      Math.max(Math.min(invoice.due, Number(this.summary?.available_credit || 0) - other), 0)
    );
    this.invalidate();
  }
  selectBankChannel(key: string) {
    const channel = this.bankChannels.find(
      (item) => `${item.bank_id}:${item.org_bank_account_id || ''}` === key
    );
    this.form.patchValue({
      bank_id: channel?.bank_id || null,
      org_bank_account_id: channel?.org_bank_account_id || null
    });
  }
  private async updateContext() {
    if (!this.isMoneyMovement || !this.isCash) {
      this.contextLabel = '';
      this.contextError = '';
      return;
    }
    if (!this.user?.id || !this.user.defaultWarehouse?.id) {
      this.contextError = 'Для наличной операции нужен склад и касса менеджера';
      return;
    }
    try {
      const context = await firstValueFrom(
        this.moneyDay.resolveCurrentMoneyDay(this.user.id, this.user.defaultWarehouse.id)
      );
      this.form.controls.cashbox_id.setValue(
        this.cashChannels.some((channel) => channel.bank_id === context.cashbox_id)
          ? context.cashbox_id
          : null
      );
      this.contextLabel = `${context.cashbox_name || 'Касса менеджера'} · ${
        context.warehouse_name || 'склад'
      } · ${context.day_id ? 'смена №' + context.day_id : 'смена откроется при операции'}`;
      this.contextError = this.form.controls.cashbox_id.value
        ? ''
        : 'Касса менеджера не связана с выбранной организацией';
    } catch (error) {
      this.contextError = accountError(error, 'Не удалось определить текущую кассу');
    }
  }
  private command(): CustomerAccountCommand {
    const value = this.form.value;
    return {
      client_id: this.data.clientId,
      org_id: this.data.orgId,
      kind: this.data.kind,
      amount: this.requiresAmount ? this.total : undefined,
      channel: this.isMoneyMovement ? value.channel : undefined,
      allocations: this.isAllocation
        ? this.invoices
            .filter((invoice) => invoice.selectedAmount > 0)
            .map((invoice) => ({
              invoice_id: invoice.invoice_id,
              amount: accountMoney(invoice.selectedAmount)
            }))
        : undefined,
      cashbox_id:
        this.isMoneyMovement && this.isCash ? Number(value.cashbox_id) || undefined : undefined,
      bank_id:
        this.isMoneyMovement && !this.isCash ? Number(value.bank_id) || undefined : undefined,
      org_bank_account_id:
        !this.isCash || this.isConfirmation
          ? Number(value.org_bank_account_id) || undefined
          : undefined,
      source_operation_id: this.data.sourceOperation?.id,
      bank_reference: value.bank_reference?.trim() || undefined,
      bank_date: accountDate(value.bank_date),
      reason: value.reason?.trim() || undefined
    };
  }
  async calculate() {
    if (!this.canPreview) return;
    this.busy = true;
    this.error = '';
    this.preview = undefined;
    this.request = undefined;
    try {
      this.preview = await firstValueFrom(this.accounts.preview(this.command()));
    } catch (error) {
      this.error = accountError(error, 'Не удалось рассчитать операцию');
    } finally {
      this.busy = false;
    }
  }
  async execute() {
    if (!this.preview || (!this.uncertain && !this.canExecute) || this.busy) return;
    this.request = this.request || {
      request_id: createRequestId(),
      token: this.preview.token,
      version: this.preview.version
    };
    this.busy = true;
    this.error = '';
    this.ref.disableClose = true;
    this.form.disable({ emitEvent: false });
    try {
      let result: any;
      if (this.uncertain) {
        try {
          result = await firstValueFrom(this.accounts.result(this.request.request_id));
        } catch (error) {
          if ((error as any)?.status !== 404) throw error;
        }
      }
      if (!result) result = await firstValueFrom(this.accounts.execute(this.request));
      this.uncertain = false;
      this.ref.close(result);
    } catch (error: any) {
      this.uncertain = !error?.status || error.status >= 500;
      this.error = this.uncertain
        ? 'Подтверждение не получено. Проверьте результат той же операции кнопкой ниже.'
        : accountError(error, 'Операция не выполнена. Обновите расчёт.');
      if (!this.uncertain) {
        this.preview = undefined;
        this.request = undefined;
        this.form.enable({ emitEvent: false });
      }
    } finally {
      this.busy = false;
      this.ref.disableClose = this.uncertain;
    }
  }
}
